
import { Options, mixins } from 'vue-class-component'
import { maska } from 'maska'
import { IRichMenu, IRichMenuConditionSetting } from '@/utils/types'
import { Watch } from 'vue-property-decorator'
import { ACTION_RICH_MENU, ACTION_RICH_MENU_CONDITION_SETTING } from '@/store/actions'
import CLoading from '@/components/common/ui/CLoading.vue'
import { constant } from '@/utils/constants'
import BaseFormMixin from '../common/mixins/BaseFormMixin.vue'
import RichMenuPreviewModal from '@/components/rich-menus/RichMenuPreviewModal.vue'
import cloneDeep from 'lodash/cloneDeep'
import ConditionSettingModal from '@/components/rich-menus/ConditionSettingModal.vue'
import DefaultRichMenuModal from '@/components/rich-menus/DefaultRichMenuModal.vue'

@Options({
  components: { DefaultRichMenuModal, ConditionSettingModal, RichMenuPreviewModal, CLoading },
  directives: { maska },
  emits: ['update:changeTab'],
})
export default class RichMenus extends mixins(BaseFormMixin) {
  loading = true
  selectedIRichMenu: IRichMenu = {}
  modalPreviewVisible = false
  conditionSettingModalVisible = false
  defaultRichMenuModalVisible = false

  conditionSetting: IRichMenuConditionSetting = {
    _id: '',
    app_id: '',
    display_setting: false,
    prod_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [],
    },
    test_detail: {
      default_rich_menu_id: '',
      priority_rich_menu: [],
    },
  }

  get richMenus() {
    return cloneDeep(this.$store.getters.richMenus) || []
  }

  get selectedAppId() {
    return this.$route.params.app_id
  }

  get activeRichMenus() {
    return this.richMenus.filter((item) => item.is_active === true)
  }

  get testRichMenus() {
    return this.richMenus.filter((item) => item.is_test_mode === true)
  }

  @Watch('selectedAppId', { immediate: true })
  async appIdChanged() {
    if (!this.selectedAppId) {
      return
    }

    await this.$store.dispatch(ACTION_RICH_MENU.LOAD_ITEMS, {
      app_id: this.selectedAppId,
    })

    const result = await this.$store.dispatch(ACTION_RICH_MENU_CONDITION_SETTING.LOAD_ITEM, {
      app_id: this.selectedAppId,
    })
    if (result._id) {
      this.conditionSetting = { ...this.conditionSetting, ...cloneDeep(result) }
    } else {
      this.conditionSetting.app_id = this.selectedAppId as string
    }

    this.loading = false
  }

  async updateIsActive(item: IRichMenu) {
    const isSuccess = await this.$store.dispatch(ACTION_RICH_MENU.IS_ACTIVE, item)
    if (isSuccess) {
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }

    return isSuccess
  }

  async updateTestMode(item: IRichMenu) {
    const isSuccess = await this.$store.dispatch(ACTION_RICH_MENU.TEST_MODE, item)
    if (isSuccess) {
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }

    return isSuccess
  }

  async handleToggleActive(record: IRichMenu) {
    if (this.conditionSetting.display_setting) {
      this.$q
        .dialog({
          title: this.$t('messages.confirm'),
          message: this.$t('messages.are_you_sure_to_turn_on_active_for_this_item'),
          cancel: {
            flat: true,
            label: this.$t('messages.no'),
          },
          ok: {
            flat: true,
            label: this.$t('messages.yes'),
          },
          persistent: true,
        })
        .onOk(async () => {
          await this.updateIsActive(record)
        })
        .onCancel(async () => {
          record.is_active = false
        })
    } else {
      const menu = this.richMenus.find((item) => item._id !== record._id && item.is_active === true)
      if (!menu) {
        if (!record.is_active) {
          await this.updateIsActive(record)
        } else {
          this.$q
            .dialog({
              title: this.$t('messages.confirm'),
              message: this.$t('messages.are_you_sure_to_turn_on_active_for_this_item'),
              cancel: {
                flat: true,
                label: this.$t('messages.no'),
              },
              ok: {
                flat: true,
                label: this.$t('messages.yes'),
              },
              persistent: true,
            })
            .onOk(async () => {
              await this.updateIsActive(record)
            })
            .onCancel(async () => {
              record.is_active = false
            })
        }
      } else {
        this.$q
          .dialog({
            title: this.$t('messages.confirm'),
            message: this.$t('messages.please_diasble_all_before_this_feature_in_enabled'),
            cancel: {
              flat: true,
              label: this.$t('cancel'),
            },
            persistent: true,
          })
          .onOk(async () => {
            menu.is_active = false
            await this.updateIsActive(menu)
          })
        record.is_active = false
      }
    }
  }

  async handleToggleTest(record: IRichMenu) {
    if (this.conditionSetting.display_setting) {
      await this.updateTestMode(record)
    } else {
      const menu = this.richMenus.find((item) => item._id !== record._id && item.is_test_mode === true)
      if (!menu) {
        await this.updateTestMode(record)
      } else {
        this.$q
          .dialog({
            title: this.$t('messages.confirm'),
            message: this.$t('messages.please_diasble_all_before_this_feature_in_enabled'),
            cancel: {
              flat: true,
              label: this.$t('cancel'),
            },
            persistent: true,
          })
          .onOk(async () => {
            menu.is_test_mode = false
            await this.updateTestMode(menu)
          })
        record.is_test_mode = false
      }
    }
  }

  onAdd() {
    this.goto('rich_menu_setting', { app_id: this.selectedAppId, action_type: constant.ACTION_TYPE.ADD })
  }

  onCopy(data: IRichMenu) {
    this.goto('rich_menu_setting', {
      app_id: this.selectedAppId,
      rich_menu_id: data._id,
      action_type: constant.ACTION_TYPE.COPY,
    })
  }

  onEdit(data: IRichMenu) {
    this.goto('rich_menu_setting', {
      app_id: this.selectedAppId,
      rich_menu_id: data._id,
      action_type: constant.ACTION_TYPE.EDIT,
    })
  }

  async onDelete(data: IRichMenu) {
    this.$q
      .dialog({
        title: this.$t('confirm'),
        message: this.$t('are_you_sure_you_want_to_delete'),
        cancel: true,
        persistent: true,
      })
      .onOk(async () => {
        const success = await this.$store.dispatch(ACTION_RICH_MENU.DELETE, data._id)
        if (success) {
          this.$q.notify({
            message: this.$t('messages.deleted'),
            color: 'positive',
          })
        }
      })
  }

  async onPreview(richMenu: IRichMenu) {
    this.selectedIRichMenu = richMenu
    this.modalPreviewVisible = true
  }

  onClosePreviewModal() {
    this.selectedIRichMenu = {}
    this.modalPreviewVisible = false
  }

  getDetailsUrl(data) {
    return this.$router.resolve({
      name: 'rich_menu_setting',
      params: {
        app_id: this.selectedAppId,
        action_type: 'edit',
        rich_menu_id: data._id,
      },
    }).href
  }

  onDetails(event, data: IRichMenu) {
    // Check if Ctrl (Windows) or Meta (Mac) is pressed
    if (event.ctrlKey || event.metaKey) {
      // Let the browser handle opening in a new tab
      return
    }
    // Otherwise, navigate programmatically
    event.preventDefault()

    this.goto('rich_menu_setting', {
      app_id: this.selectedAppId,
      action_type: 'edit',
      rich_menu_id: data._id,
    })
  }

  openConditionSetting() {
    if (!this.conditionSetting.display_setting) {
      return
    }
    this.conditionSettingModalVisible = true
  }

  updateConditionSetting(data: IRichMenuConditionSetting) {
    this.conditionSetting = data
  }

  async onChangeDisplaySetting(val, oldVal) {
    if (val === oldVal) {
      return
    }
    this.loading = true
    this.conditionSetting.display_setting = val
    if (this.conditionSetting._id === '' && this.conditionSetting.display_setting) {
      const { dispatch } = this.$store
      this.conditionSetting.test_detail = this.conditionSetting.prod_detail
      await dispatch(ACTION_RICH_MENU_CONDITION_SETTING.ADD, {
        ...this.conditionSetting,
      })
      await this.appIdChanged()
    } else {
      if (this.conditionSetting.display_setting) {
        const isSuccess = await this.$store.dispatch(ACTION_RICH_MENU_CONDITION_SETTING.IS_ACTIVE, {
          ...this.conditionSetting,
        })
        if (isSuccess) {
          this.$q.notify({
            message: this.$t('messages.saved'),
            color: 'positive',
          })
        }
        await this.appIdChanged()
      } else {
        this.defaultRichMenuModalVisible = true
      }
    }
  }

  onCloseSettingModal() {
    if (this.conditionSetting._id === '') {
      this.conditionSetting.display_setting = false
    }
    this.conditionSettingModalVisible = false
  }

  onCloseDefaultRichMenuModal() {
    this.defaultRichMenuModalVisible = false
    this.conditionSetting.display_setting = true
    this.loading = false
  }

  async setDefaultRichMenu(menu: IRichMenu) {
    this.defaultRichMenuModalVisible = false
    if (this.conditionSetting.prod_detail) {
      this.conditionSetting.prod_detail.default_rich_menu_id = menu._id
      this.conditionSetting.prod_detail.priority_rich_menu = []
    }
    if (this.conditionSetting.test_detail) {
      this.conditionSetting.test_detail.default_rich_menu_id = menu._id
      this.conditionSetting.test_detail.priority_rich_menu = []
    }

    const isSuccess = await this.$store.dispatch(ACTION_RICH_MENU_CONDITION_SETTING.IS_ACTIVE, {
      ...this.conditionSetting,
      default_rich_menu_id: menu._id,
    })

    if (isSuccess) {
      menu.is_active = true
      await this.$store.dispatch(ACTION_RICH_MENU.IS_ACTIVE, menu)
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }
    await this.appIdChanged()
  }

  goto(name: string, params = {}) {
    this.$router.push({
      name,
      params,
    })
  }
}
